import { useEffect, useState } from "react"
import ReactPaginate from "react-paginate"
import { Icon, TextField } from "@mui/material"
import moment from "moment"
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers"
import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment"

import MDTypography from "components/MDTypography"
import MDBox from "components/MDBox"
import DataTable from "components/DataTable"
import MDButton from "components/MDButton"
import { Loading } from "shared/components/ui/Loading"

import { useLazyGetTimeSheetLXODetailQuery } from "shared/redux/endpoints/lxo/teacherManagement"

// ===== Helpers =====
function getMonthDays(dateISO) {
  const m = moment(dateISO).startOf("month")
  const daysInMonth = m.daysInMonth()
  const monthDays = {}

  for (let day = 1; day <= daysInMonth; day++) {
    const key = m.clone().date(day).format("DD/MM")
    monthDays[key] = null
  }

  return {
    month: m.month() + 1,
    year: m.year(),
    days: monthDays,
  }
}

const buildTableHeaders = (dateISO) => {
  const days = getMonthDays(dateISO).days
  return [
    { Header: "Class/Event", accessor: "classEvent" },
    ...Object.keys(days).map((d) => ({ Header: d, accessor: d })),
    { Header: "Total by month", accessor: "totalHoursMonth" },
  ]
}

/**
 * API response (new):
 * {
 *   classId, totalSessions, totalMinutes, totalHours,
 *   sessions: [{ date: 'YYYY-MM-DD', hours, durationMinutes }]
 * }
 * -> rows: [{ id, classEvent, 'DD/MM': value, totalHoursMonth, ...days }]
 */
const mapToTimeSheetRows = (raw, dateISO, valueField = "hours") => {
  const daysTemplate = getMonthDays(dateISO).days

  const list = Array.isArray(raw) ? raw : raw ? [raw] : []

  return list.map((item, idx) => {
    const sessions = Array.isArray(item.sessions) ? item.sessions : []

    const row = {
      id: `${item.classId || "class"}-${idx}`,
      classEvent: item.classId || "-",
      ...daysTemplate,
      totalHoursMonth: item.totalHours ?? 0,
    }

    sessions.forEach((s) => {
      const dayKey = moment(s.date, "YYYY-MM-DD").format("DD/MM")
      if (Object.prototype.hasOwnProperty.call(row, dayKey)) {
        row[dayKey] = s?.[valueField] ?? s?.durationMinutes ?? "-"
      }
    })

    return row
  })
}

export default function TeacherManagementTimeSheetViewDetail({
  viewDetailId,
  onGoBack,
}) {
  const [isLoading, setIsLoading] = useState(true)

  const [state, setState] = useState({
    id: viewDetailId,
    // date là ngày đầu tháng
    date: moment().startOf("month").format("YYYY-MM-DD"),
    page: 0,
    start: moment().startOf("isoweek").format("MM-DD-YYYY"),
    end: moment().endOf("isoweek").format("MM-DD-YYYY"),
  })

  const [dataTable, setDataTable] = useState({
    columns: buildTableHeaders(moment().startOf("month").format("YYYY-MM-DD")),
    rows: [],
  })

  const [pagination, setPagination] = useState({
    totalPage: 0,
    currentPage: 0,
  })

  const [trigger] = useLazyGetTimeSheetLXODetailQuery()

  const handlerDataTable = (data) => {
    setDataTable((prev) => ({
      ...prev,
      rows: (data || []).map((item) => ({
        ...item,
        ...Object.entries(item).reduce((acc, [key, value]) => {
          if (key !== "id") {
            acc[key] = (
              <MDTypography variant="button" fontWeight="regular" color="text">
                {value ?? "-"}
              </MDTypography>
            )
          } else {
            acc[key] = value
          }
          return acc
        }, {}),
      })),
    }))
  }

  const handlderAPIList = (params) => {
    setIsLoading(true)

    trigger(params).then((res) => {
      const raw = res?.data?.data ?? res?.data ?? []
      const mapped = mapToTimeSheetRows(raw, params.date, "hours") // đổi sang "durationMinutes" nếu muốn

      setDataTable({ columns: buildTableHeaders(params.date), rows: [] })
      handlerDataTable(mapped)

      setPagination({
        totalPage: res?.data?.totalPage ?? 0,
        currentPage: res?.data?.currentPage ?? 0,
      })

      setIsLoading(false)
    })
  }

  const handleChangePage = (page) => {
    const next = { ...state, page }
    setState(next)
    handlderAPIList(next)
  }

  // Init load
  useEffect(() => {
    const t = setTimeout(() => {
      handlderAPIList(state)
    }, 300)
    return () => clearTimeout(t)
  }, [])

  // When month changes -> update columns
  useEffect(() => {
    setDataTable((prev) => ({
      ...prev,
      columns: buildTableHeaders(state.date),
    }))
  }, [state.date])

  return (
    <MDBox className="teacher-management__table">
      <MDBox className="teacher-management__table-header">
        <MDTypography variant="h3" className="info-icon__title">
          Timesheet detail
        </MDTypography>
      </MDBox>

      <MDBox className="teacher-management__table-filters">
        <LocalizationProvider dateAdapter={AdapterMoment}>
          <DatePicker
            views={["year", "month"]}
            openTo="month"
            value={moment(state.date, "YYYY-MM-DD")}
            onChange={(value) => {
              if (!value) return
              const monthStart = moment(value).startOf("month")
              const monthEnd = moment(value).endOf("month")
              const next = {
                ...state,
                date: monthStart.format("YYYY-MM-DD"),
                start: monthStart.format("MM-DD-YYYY"),
                end: monthEnd.format("MM-DD-YYYY"),
                page: 0,
              }
              setState(next)
              handlderAPIList(next)
            }}
            format="MM/YYYY"
            slotProps={{
              textField: {
                size: "small",
                sx: ({ typography, functions: { pxToRem } }) => ({
                  "& .MuiInputBase-root": {
                    height: pxToRem(45),
                    fontFamily: typography.lexendFont,
                  },
                }),
              },
            }}
            slots={{ textField: TextField }}
          />
        </LocalizationProvider>
      </MDBox>

      <MDBox
        className="c-teacher-management-time-sheet-view-detail teacher-management__table-wrapper"
        sx={{ position: "relative" }}
      >
        {isLoading && (
          <MDBox
            sx={{
              position: "absolute",
              inset: 0,
              zIndex: 2,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              background: "rgba(255,255,255,0.6)",
            }}
          >
            <Loading style={{ height: 120 }} />
          </MDBox>
        )}
        <DataTable
          table={dataTable}
          entriesPerPage={{
            isShown: true,
            defaultValue: 10,
            entries: [5, 10, 25],
            pagination: true,
          }}
        />

        <MDBox className="table-footer">
          <MDBox className="table-footer-showing">
            Showing {dataTable?.rows?.length} of {dataTable?.rows?.length}{" "}
            entries
          </MDBox>

          {pagination.totalPage >= 0 && (
            <ReactPaginate
              className="pagination"
              nextLabel={<Icon>chevron_right</Icon>}
              previousLabel={<Icon>chevron_left</Icon>}
              forcePage={Number(state.page || 0)}
              onPageChange={(event) => handleChangePage(event.selected)}
              pageCount={pagination.totalPage}
            />
          )}
        </MDBox>

        <MDBox className="w-full flex justify-end">
          <MDButton
            sx={{ textTransform: "none" }}
            variant="outlined"
            color="info"
            onClick={onGoBack}
          >
            Back
          </MDButton>
        </MDBox>
      </MDBox>
    </MDBox>
  )
}
